
import { useState, useMemo, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AlertCircle, Building2, Info, Wallet, ChevronLeft, ChevronRight, CreditCard, CheckCircle2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useApp } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { getCryptoIconUrl, getCryptoIconFallback } from "@/lib/cryptoIcons";

// Matches TopUpPage selection list
const cryptoOptions = [
  { currency: "USDT", name: "Tether", icon: getCryptoIconUrl("USDT") },
  { currency: "USDC", name: "USD Coin", icon: getCryptoIconUrl("USDC") },
  { currency: "TON", name: "Toncoin", icon: getCryptoIconUrl("TON") },
  { currency: "SOL", name: "Solana", icon: getCryptoIconUrl("SOL") },
  { currency: "TRX", name: "TRON", icon: getCryptoIconUrl("TRX") },
  { currency: "BTC", name: "Bitcoin", icon: getCryptoIconUrl("BTC") },
  { currency: "ETH", name: "Ethereum", icon: getCryptoIconUrl("ETH") },
];

const networkOptions = [
  // USDT
  { id: "ton", coin: "USDT", label: "The Open Network", sub: "TON", icon: getCryptoIconUrl("TON") },
  { id: "trc20", coin: "USDT", label: "Tron", sub: "TRC20", icon: getCryptoIconUrl("TRX") },
  { id: "sol", coin: "USDT", label: "Solana", sub: "SPL", icon: getCryptoIconUrl("SOL") },
  { id: "bep20", coin: "USDT", label: "BNB Smart Chain", sub: "BEP20", icon: getCryptoIconUrl("BNB") },
  { id: "erc20", coin: "USDT", label: "Ethereum", sub: "ERC20", icon: getCryptoIconUrl("ETH") },
  // USDC (ERC20)
  { id: "erc20", coin: "USDC", label: "Ethereum", sub: "ERC20", icon: getCryptoIconUrl("USDC") },
  // Native coins
  { id: "ton", coin: "TON", label: "The Open Network", sub: "TON", icon: getCryptoIconUrl("TON") },
  { id: "sol", coin: "SOL", label: "Solana", sub: "SOL", icon: getCryptoIconUrl("SOL") },
  { id: "trx", coin: "TRX", label: "Tron", sub: "TRX", icon: getCryptoIconUrl("TRX") },
  { id: "btc", coin: "BTC", label: "Bitcoin", sub: "BTC", icon: getCryptoIconUrl("BTC") },
  { id: "eth", coin: "ETH", label: "Ethereum", sub: "ETH", icon: getCryptoIconUrl("ETH") },
];

const INTERNAL_NETWORK_ID = "lightpay_internal";
const INTERNAL_ICON = "/icons/light.svg";

// Временно: фронт работает как обычно, без гейтинга по доступности провайдера
// (бэк провайдера восстановим позже). На расчёт комиссии этот флаг больше не
// влияет — она приходит из /withdraw/fee, а $2 остались только запасным
// значением на случай, если эндпоинт не ответил.
const WITHDRAW_PROVIDER_GATING = false;
const WITHDRAW_FIXED_FEE_USD = 2;
// Точность холда по монетам — зеркало digits_map в database._round_up_currency.
const HOLD_DECIMALS = { USDT: 2, USDC: 2, TON: 2, TRX: 2, SOL: 3, ETH: 4, BTC: 6 };
const EVM_ADDRESS_RE = /^0x[0-9a-fA-F]{40}$/;
const TRON_ADDRESS_RE = /^T[1-9A-HJ-NP-Za-km-z]{33}$/;
const TON_RAW_ADDRESS_RE = /^-?\d+:[0-9a-fA-F]{64}$/;
const TON_USER_ADDRESS_RE = /^[A-Za-z0-9_-]{48}$/;
const BTC_LEGACY_ADDRESS_RE = /^[13][1-9A-HJ-NP-Za-km-z]{25,34}$/;
const BTC_BECH32_ADDRESS_RE = /^bc1[ac-hj-np-z02-9]{11,71}$/i;
const BASE58_ALPHABET_RE = /^[1-9A-HJ-NP-Za-km-z]+$/;
const FIAT_RATE_FACTOR = 1.0;

const InlinePageHeader = ({ title, onBack, showBack = true }) => (
  <div className="lp-bar lp-topbar sticky top-0 z-30 px-4 py-3 grid grid-cols-[2rem_1fr_2rem] items-center border-b border-[var(--lp-hair)]">
    {showBack ? (
      <button
        type="button"
        onClick={onBack}
        className="inline-flex h-8 w-8 items-center justify-center text-foreground"
        aria-label="Back"
      >
        <ChevronLeft className="w-5 h-5" />
      </button>
    ) : (
      <span aria-hidden="true" className="h-8 w-8" />
    )}
    <h1 className="text-center text-[17px] font-semibold text-foreground">{title}</h1>
    <span aria-hidden="true" className="h-8 w-8" />
  </div>
);

const formatCardNumberInput = (raw) =>
  String(raw || "")
    .replace(/\D/g, "")
    .slice(0, 16)
    .replace(/(.{4})/g, "$1 ")
    .trim();

const normalizeCardNumber = (raw) => String(raw || "").replace(/\D/g, "");
const isValidCardNumber = (raw) => /^\d{16}$/.test(normalizeCardNumber(raw));

function validateCryptoAddressByNetwork(address, networkId) {
  const addr = String(address || "").trim();
  const net = String(networkId || "").toLowerCase();
  if (!addr) return { valid: false, message: "Введите адрес кошелька" };

  if (["erc20", "bep20", "usdc_erc20", "eth"].includes(net)) {
    return EVM_ADDRESS_RE.test(addr)
      ? { valid: true, message: "" }
      : { valid: false, message: "Некорректный EVM-адрес (0x...)" };
  }
  if (["trc20", "trx"].includes(net)) {
    return TRON_ADDRESS_RE.test(addr)
      ? { valid: true, message: "" }
      : { valid: false, message: "Некорректный TRON-адрес (должен начинаться с T)" };
  }
  if (["sol", "solana"].includes(net)) {
    const ok = addr.length >= 32 && addr.length <= 44 && BASE58_ALPHABET_RE.test(addr);
    return ok
      ? { valid: true, message: "" }
      : { valid: false, message: "Некорректный Solana-адрес" };
  }
  if (["ton", "toncoin"].includes(net)) {
    const ok = TON_RAW_ADDRESS_RE.test(addr) || TON_USER_ADDRESS_RE.test(addr);
    return ok
      ? { valid: true, message: "" }
      : { valid: false, message: "Некорректный TON-адрес" };
  }
  if (net === "btc") {
    const ok = BTC_LEGACY_ADDRESS_RE.test(addr) || BTC_BECH32_ADDRESS_RE.test(addr);
    return ok
      ? { valid: true, message: "" }
      : { valid: false, message: "Некорректный BTC-адрес" };
  }
  return { valid: false, message: "Неподдерживаемая сеть вывода" };
}

const FiatPayoutNotice = ({ t }) => (
  <div className="lp-glass px-4 py-3" role="note" data-testid="withdraw-payout-notice">
    <p className="text-sm font-medium text-foreground">{t("withdraw.payout_notice_title")}</p>
    <p className="mt-2 text-sm text-muted-foreground leading-relaxed" style={{ whiteSpace: "pre-line" }}>{t("withdraw.payout_notice_text")}</p>
  </div>
);

const SendPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { api, balances, refreshData, haptic, user, isTelegramWebApp, setBackHandler, clearBackHandler } = useApp();
  const tgUser = window.Telegram?.WebApp?.initDataUnsafe?.user || null;
  const supportHandle = tgUser?.username || user?.username || user?.first_name || 'user';
  const showInlineBack = !isTelegramWebApp;

  const activeTab = searchParams.get("tab") === "fiat" ? "fiat" : "crypto";
  const withdrawPageTitle = activeTab === "fiat"
    ? t("withdraw.title_fiat", "Withdraw RUB")
    : t("withdraw.title_crypto", "Withdraw Crypto");

  const [selectedCrypto, setSelectedCrypto] = useState(null);
  const [selectedNetwork, setSelectedNetwork] = useState(null); // New state for network selection step
  const [recipientUsername, setRecipientUsername] = useState("");
  // Рублёвый вывод — только на карту: СБП по телефону снят, экрана выбора
  // метода больше нет, фиат открывается сразу на реквизитах.
  const [fiatStep, setFiatStep] = useState(activeTab === "fiat" ? "details" : "form");

  const [address, setAddress] = useState('');
  const [memo, setMemo] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [cardHolder, setCardHolder] = useState('');
  const [amount, setAmount] = useState('');
  const [fiatUseMax, setFiatUseMax] = useState(false);
  const [fiatInputMode, setFiatInputMode] = useState('USD'); // 'USD' or 'RUB'
  const [loading, setLoading] = useState(false);
  const [feeData, setFeeData] = useState({
    fee: 0,
    fee_currency: '',
    fee_usd: 0,
    min_amount: null,
    min_amount_buffered: null,
    min_amount_currency: '',
    loading: false,
    success: null
  });
  const [config, setConfig] = useState({ usd_rub_rate: 75.1, usd_rub_withdrawal_rate: 74.0 });
  const [withdrawOptionsLoading, setWithdrawOptionsLoading] = useState(activeTab === "crypto");
  const [withdrawOptionsReady, setWithdrawOptionsReady] = useState(false);
  const [withdrawOptionsError, setWithdrawOptionsError] = useState(false);
  const [withdrawOptionsMap, setWithdrawOptionsMap] = useState({});

  useEffect(() => {
    if (activeTab === "fiat") {
      setFiatStep("details");
      setFiatInputMode("RUB");
      setAmount("");
      setCardNumber("");
      setCardHolder("");
      setFiatUseMax(false);
    } else {
      setFiatStep("form");
      setFiatUseMax(false);
    }
  }, [activeTab]);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const res = await api.get('/config');
        setConfig(res.data);
      } catch (err) {
        console.error("Failed to fetch config", err);
      }
    };
    fetchConfig();
  }, [api]);

  useEffect(() => {
    if (activeTab !== "crypto") return undefined;
    let isCancelled = false;

    const fetchWithdrawOptions = async () => {
      if (!WITHDRAW_PROVIDER_GATING) {
        // Гейтинг выключен: не ходим за options, всё доступно.
        setWithdrawOptionsMap({});
        setWithdrawOptionsReady(true);
        setWithdrawOptionsLoading(false);
        return;
      }
      setWithdrawOptionsLoading(true);
      setWithdrawOptionsError(false);
      try {
        const res = await api.get("/withdraw/options", { params: { method: "crypto" }, timeout: 4500 });
        const rows = Array.isArray(res.data?.coins) ? res.data.coins : [];
        const parsed = {};
        for (const row of rows) {
          const coinCode = String(row?.coin || "").toUpperCase();
          if (!coinCode) continue;
          const networkMap = {};
          const netRows = Array.isArray(row?.networks) ? row.networks : [];
          for (const net of netRows) {
            const netId = String(net?.network || "").toLowerCase();
            if (!netId) continue;
            networkMap[netId] = {
              available: net?.available !== false,
            };
          }
          parsed[coinCode] = { networks: networkMap };
        }
        if (!isCancelled) {
          setWithdrawOptionsMap(parsed);
          setWithdrawOptionsReady(Boolean(res.data?.success) && Object.keys(parsed).length > 0);
        }
      } catch (err) {
        console.error("Failed to fetch withdraw options", err);
        if (!isCancelled) {
          setWithdrawOptionsError(true);
          setWithdrawOptionsMap({});
          setWithdrawOptionsReady(false);
        }
      } finally {
        if (!isCancelled) {
          setWithdrawOptionsLoading(false);
        }
      }
    };

    fetchWithdrawOptions();
    return () => {
      isCancelled = true;
    };
  }, [activeTab, api]);

  // Calculation for limits
  const selectedBalance = balances.find(b => b.currency === (selectedCrypto?.currency || 'USDT')) || { amount: 0 };
  const selectedAvailableAmount = Number(selectedBalance.available ?? selectedBalance.amount ?? 0);
  const maxAmount = selectedAvailableAmount || 0;
  const isInternalTransfer = activeTab === 'crypto' && selectedNetwork === INTERNAL_NETWORK_ID;
  const withdrawalRate = config.usd_rub_withdrawal_rate || 74.0;
  const fiatWithdrawFeePct = Math.max(0, Number(config.fiat_withdraw_fee_pct ?? 1.5));
  const fiatWithdrawFeeFactor = fiatWithdrawFeePct / 100;
  const fiatWithdrawFeeMultiplier = 1 + fiatWithdrawFeeFactor;
  const fiatRate = Math.max(0.0000001, Number(withdrawalRate) * FIAT_RATE_FACTOR);
  const amountNum = parseFloat(amount) || 0;
  const fiatAmountRub = activeTab === "fiat"
    ? (fiatInputMode === "USD" ? ((amountNum > 0 ? (amountNum / fiatWithdrawFeeMultiplier) : 0) * fiatRate) : amountNum)
    : 0;
  const fiatAmountUsdBase = activeTab === "fiat"
    ? (fiatAmountRub > 0 ? fiatAmountRub / fiatRate : 0)
    : 0;
  const fiatDebitUsdt = activeTab === "fiat"
    ? (fiatInputMode === "USD"
      ? Math.max(0, amountNum)
      : (fiatAmountUsdBase > 0 ? fiatAmountUsdBase * fiatWithdrawFeeMultiplier : 0))
    : 0;
  const fiatFeeUsd = fiatDebitUsdt > 0 ? Math.max(0, fiatDebitUsdt - fiatAmountUsdBase) : 0;
  const cardNumberNormalized = normalizeCardNumber(cardNumber);
  const cardNumberValid = isValidCardNumber(cardNumber);
  const feeUnavailable = feeData.success === false || feeData.fee === null || Number.isNaN(Number(feeData.fee));
  const feeKnown = feeData.success === true && Number.isFinite(Number(feeData.fee));
  const feeCoin = feeKnown ? Number(feeData.fee || 0) : 0;
  const cryptoDebitAmount = feeKnown ? (amountNum + feeCoin) : null;
  const formatCryptoAmount = (val) => {
    const num = Number(val) || 0;
    return num < 1 ? num.toFixed(4) : num.toFixed(2);
  };
  const formatRub = (val) => {
    const num = Number(val);
    if (!Number.isFinite(num)) return "—";
    return Math.floor(num).toLocaleString("ru-RU");
  };
  const formatInputNumber = (val, maxDecimals = 8) => {
    const num = Number(val);
    if (!Number.isFinite(num) || num <= 0) return "";
    return num.toFixed(maxDecimals).replace(/\.?0+$/, "");
  };
  const floorTo = (val, decimals = 2) => {
    const num = Number(val);
    if (!Number.isFinite(num) || num <= 0) return 0;
    const factor = 10 ** Math.max(0, decimals);
    return Math.floor((num + 1e-9) * factor) / factor;
  };
  const roundTo = (val, decimals = 2) => {
    const num = Number(val);
    if (!Number.isFinite(num) || num <= 0) return 0;
    const factor = 10 ** Math.max(0, decimals);
    return Math.round((num + Number.EPSILON) * factor) / factor;
  };
  // When enforcing minimums, round up to avoid showing a value that the backend still rejects.
  const formatMinAmount = (val) => {
    const num = Number(val);
    if (!isFinite(num) || num <= 0) return "0";
    const decimals = num < 1 ? 6 : 2;
    const factor = Math.pow(10, decimals);
    const up = Math.ceil(num * factor) / factor;
    return up.toFixed(decimals).replace(/\.?0+$/, "");
  };

  const fiatMinRub = Math.max(0, Number(config.fiat_min_withdrawal_rub ?? 0));
  const fiatMinUsd = fiatMinRub > 0 ? (fiatMinRub / fiatRate) * fiatWithdrawFeeMultiplier : Number(config.fiat_min_withdrawal_usd ?? 1);
  const fiatMaxRub = Math.max(0, Number(config.fiat_max_withdrawal_rub ?? 1_000_000));
  const fiatMaxUsd = fiatMaxRub > 0 ? (fiatMaxRub / fiatRate) * fiatWithdrawFeeMultiplier : 0;
  const maxRubByBalance = Math.max(0, (maxAmount / fiatWithdrawFeeMultiplier) * fiatRate);
  const maxWithdrawRub = Math.max(0, Math.min(fiatMaxRub, maxRubByBalance));
  const maxWithdrawUsd = maxWithdrawRub > 0 ? ((maxWithdrawRub / fiatRate) * fiatWithdrawFeeMultiplier) : 0;
  const maxWithdrawRubInput = floorTo(maxWithdrawRub, 2);
  const maxWithdrawUsdInput = floorTo(maxWithdrawUsd, 2);
  const fiatRateLabel = `${formatInputNumber(fiatRate, 2) || "0"} ₽ = 1 USDT`;
  const fiatMaxDebitUsdt = Math.min(maxAmount, fiatMaxUsd);
  const effectiveDebitUsdt = activeTab === "fiat" && fiatUseMax ? fiatMaxDebitUsdt : fiatDebitUsdt;
  const effectiveBaseUsd = effectiveDebitUsdt > 0 ? (effectiveDebitUsdt / fiatWithdrawFeeMultiplier) : 0;
  const effectiveFeeUsd = effectiveDebitUsdt > 0 ? (effectiveDebitUsdt - effectiveBaseUsd) : 0;
  const uiDebitUsdt = roundTo(effectiveDebitUsdt, 2);
  const uiFeeUsd = roundTo(effectiveFeeUsd, 2);
  const usdEquivalent = activeTab === 'fiat'
    ? (fiatUseMax ? fiatMaxDebitUsdt : fiatDebitUsdt)
    : amountNum;
  const isBelowFiatMinRub = activeTab === 'fiat' && fiatMinRub > 0 && fiatAmountRub > 0 && fiatAmountRub < fiatMinRub;
  const isBelowFiatMin = activeTab === 'fiat' && fiatAmountRub > 0 && (isBelowFiatMinRub || usdEquivalent < fiatMinUsd);
  const isOutOfRange = activeTab === 'fiat' && fiatAmountRub > 0 && (
    isBelowFiatMin || usdEquivalent > fiatMaxUsd || fiatAmountRub > fiatMaxRub
  );
  const rapiraMinInput = Number(feeData.min_amount ?? 0);
  const cryptoMin = rapiraMinInput > 0
    ? rapiraMinInput
    : Number(feeData.min_amount_buffered ?? 0);
  const isBelowCryptoMin = activeTab === 'crypto' && !isInternalTransfer && cryptoMin > 0 && amountNum > 0 && amountNum < cryptoMin;
  const cryptoBalanceSpend = activeTab === 'crypto' && !isInternalTransfer
    ? amountNum + feeCoin
    : amountNum;
  // Бэк округляет холд (сумма + комиссия) ВВЕРХ до точности монеты
  // (database._round_up_currency), поэтому просто «вычесть комиссию» мало:
  // хвост вида 99.80000000000001 после округления перевешивает баланс и заявка
  // падает в insufficient_funds. Округление вверх влезает в баланс ровно тогда,
  // когда списание не превышает баланс, УРЕЗАННЫЙ до той же точности, — по
  // этому порогу и считаем максимум и проверяем нехватку средств.
  const holdDecimals = HOLD_DECIMALS[String(selectedCrypto?.currency || '').toUpperCase()] ?? 8;
  const cryptoSpendCeiling = activeTab === 'crypto' && !isInternalTransfer
    ? floorTo(selectedAvailableAmount, holdDecimals)
    : selectedAvailableAmount;
  const isCryptoInsufficient = activeTab === 'crypto' && amountNum > 0 && cryptoBalanceSpend > cryptoSpendCeiling + 1e-9;
  const hasNoCoinBalance = activeTab === 'crypto' && Boolean(selectedCrypto) && selectedAvailableAmount <= 0;
  const shouldSuggestSwap = hasNoCoinBalance || isCryptoInsufficient;
  const maxInputAmount = activeTab === 'crypto' && !isInternalTransfer
    ? floorTo(cryptoSpendCeiling - feeCoin, holdDecimals)
    : selectedAvailableAmount;
  const normalizedAddress = String(address || "").trim();
  const shouldValidateAddressFormat =
    activeTab === "crypto" &&
    !isInternalTransfer &&
    Boolean(selectedNetwork) &&
    normalizedAddress.length > 0;
  const addressFormatCheck = shouldValidateAddressFormat
    ? validateCryptoAddressByNetwork(normalizedAddress, selectedNetwork)
    : { valid: false, message: "" };
  const isAddressFormatInvalid = shouldValidateAddressFormat && !addressFormatCheck.valid;
  const isAddressFormatValid = shouldValidateAddressFormat && addressFormatCheck.valid;

  const bestFromCoin = useMemo(() => {
    const target = String(selectedCrypto?.currency || "").toUpperCase();
    const candidates = balances
      .map((b) => ({
        currency: String(b?.currency || "").toUpperCase(),
        available: Number(b?.available ?? b?.amount ?? 0),
      }))
      .filter((item) => item.currency && item.currency !== target && item.available > 0)
      .sort((a, b) => b.available - a.available);
    return candidates[0]?.currency || "USDT";
  }, [balances, selectedCrypto]);

  useEffect(() => {
    // Комиссия не зависит ни от суммы, ни от адреса (бэк считает её по монете и
    // сети), а вот кнопка MAX зависит от комиссии. Поэтому тянем её сразу после
    // выбора сети, не дожидаясь ввода: иначе первый тап по MAX подставлял полный
    // баланс, комиссия приезжала следом и сумма оказывалась неподъёмной.
    if (
      activeTab === 'crypto' &&
      selectedCrypto &&
      selectedNetwork &&
      selectedNetwork !== INTERNAL_NETWORK_ID
    ) {
      // Комиссию всегда спрашиваем у бэка: /withdraw/fee отдаёт сумму комиссии
      // сети ПЛЮС надбавку платформы, то есть ровно то, что /withdraw удержит
      // с баланса сверх суммы вывода. Фиксированные $2 учитывали только вторую
      // часть, поэтому на сетях с ненулевой комиссией (TRC20 — 2 USDT, ERC20 —
      // 0.5) кнопка MAX выдавала сумму, на которую бэку не хватало холда, и
      // заявка падала в 400 insufficient_funds. Гейтинг доступности провайдера
      // (WITHDRAW_PROVIDER_GATING) к расчёту комиссии отношения не имеет.
      const applyFixedFeeFallback = () => {
        const rate = Number(selectedBalance?.usd_rate || 0);
        const feeInCoin = rate > 0 ? WITHDRAW_FIXED_FEE_USD / rate : WITHDRAW_FIXED_FEE_USD;
        setFeeData({
          fee: feeInCoin,
          fee_currency: selectedCrypto?.currency || '',
          fee_usd: WITHDRAW_FIXED_FEE_USD,
          min_amount: null,
          min_amount_buffered: null,
          min_amount_currency: selectedCrypto?.currency || '',
          loading: false,
          success: true
        });
      };

      const fetchFees = async () => {
        setFeeData(prev => ({ ...prev, loading: true }));
        try {
          const res = await api.get('/withdraw/fee', {
            params: {
              currency: selectedCrypto.currency,
              amount: amountNum || 0,
              address: address || '',
              network: selectedNetwork
            }
          });
          if (res.data.success) {
            setFeeData({
              fee: Number(res.data.fee || 0),
              fee_currency: res.data.fee_currency || selectedCrypto?.currency || '',
              fee_usd: Number(res.data.fee_usd || 0),
              min_amount: res.data.min_amount ?? null,
              min_amount_buffered: res.data.min_amount_buffered ?? null,
              min_amount_currency: res.data.min_amount_currency || selectedCrypto?.currency || '',
              loading: false,
              success: true
            });
          } else {
            setFeeData({
              fee: res.data.fee ?? null,
              fee_currency: res.data.fee_currency || selectedCrypto?.currency || '',
              fee_usd: res.data.fee_usd ?? null,
              min_amount: res.data.min_amount ?? null,
              min_amount_buffered: res.data.min_amount_buffered ?? null,
              min_amount_currency: res.data.min_amount_currency || selectedCrypto?.currency || '',
              loading: false,
              success: false
            });
          }
        } catch (err) {
          // Сеть/бэк не ответили — не запираем вывод совсем, откатываемся на
          // прежнюю фиксированную оценку. Явный success:false от бэка (например
          // «Unsupported network») в этот откат НЕ попадает: там отказ по делу.
          console.error("Fee fetch failed", err);
          applyFixedFeeFallback();
        }
      };

      const timer = setTimeout(fetchFees, 500);
      return () => clearTimeout(timer);
    } else {
      setFeeData({
        fee: 0,
        fee_currency: '',
        fee_usd: 0,
        min_amount: null,
        min_amount_buffered: null,
        min_amount_currency: '',
        loading: false,
        success: null
      });
    }
  }, [api, activeTab, amountNum, address, selectedCrypto, selectedNetwork, selectedBalance?.usd_rate]);

  const getNetworkAvailability = useCallback((coinCode, networkId) => {
    if (String(networkId || "") === INTERNAL_NETWORK_ID) {
      return { available: true };
    }
    if (!WITHDRAW_PROVIDER_GATING) return { available: true };
    if (!withdrawOptionsReady) return { available: false };
    const row = withdrawOptionsMap[String(coinCode || "").toUpperCase()];
    if (!row) {
      return { available: false };
    }
    const network = row.networks?.[String(networkId || "").toLowerCase()];
    if (!network) {
      return { available: false };
    }
    return {
      available: network.available !== false,
    };
  }, [withdrawOptionsReady, withdrawOptionsMap]);

  const isCoinProviderAvailable = useCallback((coinCode) => {
    const coin = String(coinCode || "").toUpperCase();
    const coinNetworks = networkOptions.filter((n) => n.coin === coin);
    if (!coinNetworks.length) return false;
    return coinNetworks.some((n) => getNetworkAvailability(coin, n.id).available);
  }, [getNetworkAvailability]);

  // Filtered cryptos for selection step (unavailable are hidden).
  const availableCryptos = useMemo(
    () => cryptoOptions.filter((c) => isCoinProviderAvailable(c.currency)),
    [isCoinProviderAvailable]
  );


  const handleBack = useCallback(() => {
    haptic('light');
    if (activeTab === 'crypto') {
      if (selectedNetwork) {
        setSelectedNetwork(null); // Go back to Network Selection
        setRecipientUsername("");
      } else if (selectedCrypto) {
        setSelectedCrypto(null); // Go back to Coin Selection
      } else {
        navigate(-1);
      }
    } else {
      if (fiatStep === "confirm") {
        setFiatStep("details");
      } else {
        navigate(-1);
      }
    }
  }, [haptic, activeTab, selectedNetwork, selectedCrypto, fiatStep, navigate]);

  useEffect(() => {
    if (!isTelegramWebApp) return;
    setBackHandler(handleBack);
    return () => clearBackHandler();
  }, [isTelegramWebApp, setBackHandler, clearBackHandler, handleBack]);

  const availableNetworks = useMemo(() => {
    if (!selectedCrypto) return [];
    const all = networkOptions.filter((n) => n.coin === selectedCrypto.currency);
    const filtered = all.filter((n) => {
      return getNetworkAvailability(selectedCrypto.currency, n.id).available;
    });
    return [
      ...filtered,
      {
        id: INTERNAL_NETWORK_ID,
        coin: selectedCrypto.currency,
        label: "LightPay",
        sub: "Внутренний перевод без комиссии",
        icon: INTERNAL_ICON,
        isInternal: true,
      },
    ];
  }, [selectedCrypto, getNetworkAvailability]);

  const selectedNetworkMeta = useMemo(
    () => availableNetworks.find((n) => n.id === selectedNetwork) || null,
    [availableNetworks, selectedNetwork]
  );

  useEffect(() => {
    if (!selectedCrypto) return;
    if (!isCoinProviderAvailable(selectedCrypto.currency)) {
      setSelectedCrypto(null);
      setSelectedNetwork(null);
    }
  }, [selectedCrypto, isCoinProviderAvailable]);

  useEffect(() => {
    if (!selectedCrypto || !selectedNetwork || selectedNetwork === INTERNAL_NETWORK_ID) return;
    const availability = getNetworkAvailability(selectedCrypto.currency, selectedNetwork);
    if (!availability.available) {
      setSelectedNetwork(null);
    }
  }, [selectedCrypto, selectedNetwork, getNetworkAvailability]);

  const handleSelectCrypto = (crypto) => {
    if (!isCoinProviderAvailable(crypto?.currency)) {
      toast.message(t("withdraw.temporarily_unavailable"));
      return;
    }
    haptic('light');
    setSelectedCrypto(crypto);
    setSelectedNetwork(null);
    setRecipientUsername("");
  };

  const handleSelectNetwork = (networkId) => {
    const availability = getNetworkAvailability(selectedCrypto?.currency, networkId);
    if (!availability.available) return;
    haptic('light');
    if (networkId === INTERNAL_NETWORK_ID) {
      setAddress("");
      setMemo("");
    } else {
      setRecipientUsername("");
    }
    setSelectedNetwork(networkId);
  };

  const handleGoToSwap = () => {
    const toCoin = String(selectedCrypto?.currency || "").toUpperCase();
    const params = new URLSearchParams();
    if (toCoin) params.set("to", toCoin);
    if (bestFromCoin) params.set("from", bestFromCoin);
    haptic('light');
    navigate(`/swap?${params.toString()}`);
  };

  const handleSend = async () => {
    haptic('medium');

    if (activeTab === 'fiat' && (isBelowFiatMin || fiatAmountRub > fiatMaxRub || usdEquivalent > fiatMaxUsd)) {
      if (isBelowFiatMin) {
        toast.error(`Минимальная сумма вывода: ${formatRub(fiatMinRub)} ₽`);
      } else {
        toast.error(`Лимит вывода RUB: ${formatRub(fiatMinRub)}-${formatRub(fiatMaxRub)} ₽ за заявку`);
      }
      return;
    }

    const sendAmountStr = activeTab === 'fiat'
      ? effectiveDebitUsdt.toFixed(2)
      : amount;

    const sendAmount = parseFloat(sendAmountStr);
    if (isNaN(sendAmount) || sendAmount <= 0 || (activeTab === "fiat" && fiatAmountRub <= 0)) {
      toast.error(t('common.valid_amount_error'));
      return;
    }

    if (activeTab === 'crypto' && !isInternalTransfer) {
      if (!feeKnown || feeData.loading) {
        toast.error("Не удалось рассчитать комиссию, попробуйте еще раз");
        return;
      }
      const debitAmount = sendAmount + feeCoin;
      const minInput = Number(cryptoMin || 0);
      if (minInput > 0 && sendAmount < minInput) {
        toast.error(`Minimum withdrawal is ${formatMinAmount(minInput)} ${feeData.min_amount_currency || (selectedCrypto?.currency || '')}`);
        return;
      }
      if (debitAmount > cryptoSpendCeiling + 1e-9) {
        toast.error(t('withdraw.insufficient_balance'));
        return;
      }
    } else if (sendAmount > maxAmount) {
      toast.error(t('withdraw.insufficient_balance'));
      return;
    }

    let amountUsd = sendAmount;
    let amountCrypto = null;
    if (activeTab === 'crypto') {
      const amountForProvider = sendAmount;
      const rate = Number(selectedBalance?.usd_rate || 0);
      amountUsd = rate ? (amountForProvider * rate) : amountForProvider;
      amountCrypto = amountForProvider;
    }

    if (activeTab === 'crypto') {
      if (isInternalTransfer) {
        const normalizedRecipient = recipientUsername.trim().replace(/^@+/, "");
        if (!/^[A-Za-z0-9_]{5,32}$/.test(normalizedRecipient)) {
          toast.error("Укажите корректный Telegram username");
          return;
        }
      } else {
        const normalizedAddress = address.trim();
        if (!normalizedAddress || normalizedAddress.length < 5) {
          toast.error(t('withdraw.valid_address_error'));
          return;
        }
        const addrCheck = validateCryptoAddressByNetwork(normalizedAddress, selectedNetwork);
        if (!addrCheck.valid) {
          toast.error(addrCheck.message || t('withdraw.valid_address_error'));
          return;
        }
      }
    } else {
      if (!cardNumberValid) {
        toast.error("Введите корректный номер карты РФ");
        return;
      }
    }

    setLoading(true);
    try {
      const normalizedRecipient = recipientUsername.trim().replace(/^@+/, "");
      let finalAddress = address;
      if (activeTab === 'fiat') {
        const parts = [
          "Method: Card",
          fiatAmountRub > 0 ? `Rub: ${fiatAmountRub.toFixed(2)}` : null,
          `Card: ${cardNumberNormalized}`,
          cardHolder.trim() ? `Name: ${cardHolder.trim()}` : null,
        ].filter(Boolean);
        finalAddress = parts.join(", ");
      } else if (activeTab === 'crypto' && !isInternalTransfer) {
        finalAddress = address.trim();
      }

      if (activeTab === 'crypto' && isInternalTransfer) {
        await api.post('/transfer/internal', {
          user_id: user.id,
          recipient_username: normalizedRecipient,
          amount: amountCrypto,
          currency: selectedCrypto.currency,
        });
      } else {
        const payload = {
          user_id: user.id,
          amount_usd: amountUsd,
          method: activeTab,
          address: finalAddress,
          memo: memo,
        };
        if (activeTab === "fiat") {
          payload.use_max_balance = Boolean(fiatUseMax);
          payload.fiat_input_mode = fiatInputMode;
        }

        if (activeTab === 'crypto') {
          payload.coin = selectedCrypto.currency;
          payload.network = selectedNetwork;
          payload.amount_crypto = amountCrypto;
        }

        await api.post('/withdraw', payload);
      }

      toast.success(isInternalTransfer ? "Перевод отправлен" : t('withdraw.success'));
      await refreshData();
      setRecipientUsername("");
      navigate('/');
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail === "recipient_not_found") {
        toast.error("Пользователь с таким username не найден");
        return;
      }
      if (detail === "self_transfer_not_allowed") {
        toast.error("Нельзя переводить самому себе");
        return;
      }
      // Бэк отдаёт в detail внутренний код (insufficient_funds и т.п.) — показывать
      // его юзеру бессмысленно, тем более что причина обычно в комиссии, которая
      // берётся сверх суммы вывода.
      if (detail === "insufficient_funds") {
        toast.error(
          activeTab === 'crypto' && !isInternalTransfer && feeKnown
            ? `Недостаточно средств: с комиссией ${formatCryptoAmount(feeCoin)} ${selectedCrypto?.currency || 'USDT'} спишется ${formatCryptoAmount(cryptoBalanceSpend)}, доступно ${formatCryptoAmount(cryptoSpendCeiling)}`
            : t('withdraw.insufficient_balance')
        );
        return;
      }
      if (detail === "bad_amount" || detail === "coin_required" || detail === "rate_unavailable") {
        toast.error(t('common.valid_amount_error'));
        return;
      }
      const msg = detail || (isInternalTransfer ? "Transfer failed" : "Withdrawal failed");
      toast.error(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setLoading(false);
    }
  };

  const handleMax = () => {
    haptic('light');
    if (activeTab === 'fiat') {
      if (fiatInputMode === "USD") {
        setAmount(formatInputNumber(maxWithdrawUsdInput, 2));
      } else {
        setAmount(formatInputNumber(maxWithdrawRubInput, 2));
      }
      setFiatUseMax(true);
      return;
    }
    setAmount(maxInputAmount.toString());
  };

  // --- RENDER STEPS ---

  // 1. Coin Selection
  if (activeTab === "fiat" && config.fiat_withdraw_maintenance !== false) {
    return (
      <div className="h-[100dvh] bg-background flex flex-col animate-fade-in" data-testid="withdraw-maintenance-page">
        <InlinePageHeader title={withdrawPageTitle} onBack={handleBack} showBack={showInlineBack} />
        <section className="flex-1 flex flex-col items-center justify-center px-6 pb-24 text-center" role="status">
          <div className="text-4xl mb-5" aria-hidden="true">🛠️</div>
          <h1 className="text-xl font-semibold text-foreground">{t("withdraw.maintenance_title")}</h1>
          <p className="mt-3 max-w-xs text-sm leading-relaxed text-muted-foreground">{t("withdraw.maintenance_text")}</p>
          <button type="button" className="lp-press lp-glass mt-6 rounded-xl px-5 py-3 text-sm font-semibold text-foreground" onClick={() => navigate("/")}>{t("withdraw.maintenance_back")}</button>
        </section>
      </div>
    );
  }

  if (activeTab === 'crypto' && withdrawOptionsLoading && !withdrawOptionsReady) {
    return (
      <div className="h-[100dvh] bg-background flex flex-col animate-fade-in">
        <InlinePageHeader title={withdrawPageTitle} onBack={handleBack} showBack={showInlineBack} />
        <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm">
          {t("common.loading")}
        </div>
      </div>
    );
  }

  // 1. Coin Selection
  if (activeTab === 'crypto' && !selectedCrypto) {
    return (
      <div className="h-[100dvh] bg-background flex flex-col animate-fade-in" data-testid="send-select-page">
        <InlinePageHeader title={withdrawPageTitle} onBack={handleBack} showBack={showInlineBack} />

        <div className="flex-1 overflow-y-auto pb-24">
          <div className="p-4">
            <div className="space-y-1">
              {availableCryptos.map((crypto) => {
                return (
                  <button
                    key={crypto.currency}
                    onClick={() => handleSelectCrypto(crypto)}
                    className="w-full flex items-center p-3 rounded-xl transition-colors"
                  >
                    <img
                      src={crypto.icon}
                      alt={crypto.currency}
                      className="w-10 h-10 rounded-full mr-3"
                      loading="lazy"
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = getCryptoIconFallback(crypto.currency);
                      }}
                    />
                    <div className="flex-1 text-left">
                      <div className="font-semibold flex items-center gap-2">{crypto.currency}</div>
                      <div className="text-sm text-muted-foreground">{crypto.name}</div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-300" /> {/* Chevron Right for drill-down feel */}
                  </button>
                );
              })}
              {availableCryptos.length === 0 && (
                <div className="text-sm text-muted-foreground py-4">
                  {!withdrawOptionsReady && !withdrawOptionsError ? t("common.loading") : t("withdraw.temporarily_unavailable")}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 2. Network Selection (New Step)
  if (activeTab === 'crypto' && selectedCrypto && !selectedNetwork) {
    return (
      <div className="min-h-screen bg-background flex flex-col animate-fade-in" data-testid="send-network-page">
        <InlinePageHeader title={withdrawPageTitle} onBack={handleBack} showBack={showInlineBack} />

        <div className="p-6 flex-1">
          <label className="text-sm text-muted-foreground mb-2 block">{t('topup.network')}</label>
          <div className="space-y-1">
            {availableNetworks.map((n) => {
              return (
                <button
                  key={n.id}
                  onClick={() => handleSelectNetwork(n.id)}
                  className="w-full flex items-center p-3 rounded-xl transition-colors"
                >
                  <img
                    src={n.icon}
                    alt={n.label}
                    className="w-10 h-10 rounded-full mr-3"
                    loading="lazy"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      if (n.isInternal) {
                        e.currentTarget.src = INTERNAL_ICON;
                        return;
                      }
                      e.currentTarget.src = getCryptoIconFallback(n.sub || n.label);
                    }}
                  />
                  <div className="flex-1 text-left">
                    <div className="font-semibold flex items-center gap-2">{n.label}</div>
                    <div className="text-sm text-muted-foreground">
                      {n.sub}
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-300" />
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  if (activeTab === "fiat" && fiatStep === "details") {
    return (
      <div className="h-[100dvh] bg-background flex flex-col animate-fade-in" data-testid="withdraw-rub-card-page">
        <InlinePageHeader title="Вывод на карту" onBack={handleBack} showBack={showInlineBack} />
        <div className="flex-1 overflow-y-auto pb-24">
          <div className="p-6 space-y-5">
            <div className="lp-glass px-4 py-3">
              <p className="text-xs text-muted-foreground">Баланс USDT</p>
              <p className="text-2xl font-semibold text-foreground mt-1">
                {Number(maxAmount || 0).toFixed(2)} USDT
              </p>
            </div>

            <FiatPayoutNotice t={t} />

            <div className="space-y-2">
              <label className="text-sm text-muted-foreground">Номер карты РФ</label>
              <Input
                value={cardNumber}
                onChange={(e) => setCardNumber(formatCardNumberInput(e.target.value))}
                placeholder="0000 0000 0000 0000"
                inputMode="numeric"
                className={cn("h-12 rounded-xl", cardNumber && !cardNumberValid && "border-red-500")}
              />
              {cardNumber && !cardNumberValid ? (
                <p className="text-xs text-red-500">Введите корректный номер карты РФ</p>
              ) : null}
            </div>
            <div className="space-y-2">
              <label className="text-sm text-muted-foreground">Имя получателя (опционально)</label>
              <Input
                value={cardHolder}
                onChange={(e) => setCardHolder(e.target.value)}
                placeholder="Иван Иванов"
                className="h-12 rounded-xl"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-sm text-muted-foreground">
                  {fiatInputMode === "USD" ? "Сумма в долларах" : "Сумма в рублях"}
                </label>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => {
                      if (fiatInputMode === "USD") return;
                      const current = parseFloat(amount) || 0;
                      setFiatInputMode("USD");
                      setAmount(current > 0 ? formatInputNumber((current / fiatRate) * fiatWithdrawFeeMultiplier, 2) : "");
                      setFiatUseMax(false);
                    }}
                    className={cn("px-2 py-0.5 text-xs font-semibold rounded-md", fiatInputMode === "USD" ? "text-link" : "text-muted-foreground")}
                  >
                    USD
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (fiatInputMode === "RUB") return;
                      const current = parseFloat(amount) || 0;
                      setFiatInputMode("RUB");
                      setAmount(current > 0 ? formatInputNumber((current / fiatWithdrawFeeMultiplier) * fiatRate, 2) : "");
                      setFiatUseMax(false);
                    }}
                    className={cn("px-2 py-0.5 text-xs font-semibold rounded-md", fiatInputMode === "RUB" ? "text-link" : "text-muted-foreground")}
                  >
                    RUB
                  </button>
                </div>
              </div>
              <Input
                type="number"
                value={amount}
                onChange={(e) => {
                  setAmount(e.target.value);
                  setFiatUseMax(false);
                }}
                onWheel={(e) => e.currentTarget.blur()}
                inputMode="decimal"
                placeholder="0"
                className={cn(
                  "h-12 rounded-xl",
                  (isBelowFiatMin || fiatAmountRub > fiatMaxRub) && "border-red-500 ring-red-500 focus-visible:ring-red-500"
                )}
              />
              {isBelowFiatMin ? (
                <div className="flex items-center gap-2 rounded-xl border border-red-500/25 bg-red-500/10 px-3 py-2 text-xs font-medium text-red-500">
                  <AlertCircle className="h-4 w-4 shrink-0" />
                  <span>Минимальная сумма вывода: {formatRub(fiatMinRub)} ₽</span>
                </div>
              ) : null}
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">
                  {fiatInputMode === "USD"
                    ? `≈ ${fiatAmountRub > 0 ? fiatAmountRub.toFixed(2) : "0.00"} RUB`
                    : "\u00A0"}
                </span>
                <button
                  type="button"
                  onClick={handleMax}
                  className="text-xs text-link font-medium"
                >
                  MAX: {fiatInputMode === "USD" ? formatInputNumber(maxWithdrawUsdInput, 2) : formatInputNumber(maxWithdrawRubInput, 2)}
                </button>
              </div>
            </div>

            <div className="rounded-2xl border border-dashed border-[rgb(var(--lp-edge)/var(--lp-edge-a))] bg-[var(--lp-tile)] px-4 py-3">
              <div className="flex items-center">
                <p className="text-xs text-muted-foreground inline-flex items-center">
                  Спишется с баланса
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        className="ml-1 text-[11px] font-semibold leading-none text-muted-foreground transition-colors hover:text-foreground"
                        aria-label="Показать комиссию"
                      >
                        *
                      </button>
                    </PopoverTrigger>
                    <PopoverContent side="right" align="start" sideOffset={4} className="w-auto max-w-[220px] -translate-x-1 px-2.5 py-1.5 text-[11px]">
                      С учетом комиссии
                      <br />
                      конвертации.
                    </PopoverContent>
                  </Popover>
                </p>
              </div>
              <p className="text-lg font-semibold text-foreground mt-1">
                {fiatAmountRub > 0 ? `${uiDebitUsdt.toFixed(2)} USDT` : "0.00 USDT"}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Курс: {fiatRateLabel}
              </p>
            </div>
            {fiatAmountRub > fiatMaxRub ? (
              <p className="text-xs text-red-500">Максимум за одну заявку: {formatRub(fiatMaxRub)} ₽</p>
            ) : null}

            <Button
              type="button"
              className="w-full h-12 rounded-xl"
              disabled={
                !cardNumberValid ||
                fiatAmountRub <= 0 ||
                effectiveDebitUsdt > (maxAmount + 1e-9) ||
                isOutOfRange
              }
              onClick={() => {
                haptic("medium");
                setFiatStep("confirm");
              }}
            >
              Продолжить
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (activeTab === "fiat" && fiatStep === "confirm") {
    return (
      <div className="h-[100dvh] bg-background flex flex-col animate-fade-in" data-testid="withdraw-rub-confirm-page">
        <InlinePageHeader title="Подтверждение" onBack={handleBack} showBack={showInlineBack} />
        <div className="flex-1 overflow-y-auto pb-24">
          <div className="p-6 space-y-4">
            <div className="lp-glass px-4 py-3">
              <p className="text-xs text-muted-foreground">Баланс USDT</p>
              <p className="text-2xl font-semibold text-foreground mt-1">
                {Number(maxAmount || 0).toFixed(2)} USDT
              </p>
            </div>
            <FiatPayoutNotice t={t} />

            <div className="lp-glass divide-y divide-[var(--lp-hair)]">
              <div className="px-4 py-3">
                <p className="text-xs text-muted-foreground">Номер карты</p>
                <p className="text-xl font-semibold text-foreground mt-1">{formatCardNumberInput(cardNumber)}</p>
              </div>
              <div className="px-4 py-3">
                <p className="text-xs text-muted-foreground">Сумма</p>
                <p className="text-xl font-semibold text-foreground mt-1">{fiatAmountRub.toFixed(2)} ₽</p>
              </div>
              <div className="px-4 py-3">
                <p className="text-xs text-muted-foreground">Курс</p>
                <p className="text-xl font-semibold text-foreground mt-1">{fiatRateLabel}</p>
              </div>
              <div className="px-4 py-3">
                <p className="text-xs text-muted-foreground">Комиссия ({fiatWithdrawFeePct.toFixed(1)}%)</p>
                <p className="text-xl font-semibold text-foreground mt-1">{uiFeeUsd.toFixed(2)} USDT</p>
              </div>
              <div className="px-4 py-3">
                <p className="text-xs text-muted-foreground">Итого</p>
                <p className="text-xl font-semibold text-foreground mt-1">{uiDebitUsdt.toFixed(2)} USDT</p>
              </div>
            </div>

            <Button
              type="button"
              onClick={handleSend}
              disabled={
                loading ||
                !cardNumberValid ||
                fiatAmountRub <= 0 ||
                effectiveDebitUsdt > (maxAmount + 1e-9) ||
                isOutOfRange
              }
              className="w-full h-12 rounded-xl"
            >
              {loading ? <div className="spinner mr-2" /> : <CheckCircle2 className="w-5 h-5 mr-2" />}
              Подтвердить
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // 3. Form Step (Crypto Form or Fiat Form)
  return (
    <div className="h-[100dvh] bg-background flex flex-col animate-fade-in" data-testid="send-form-page">
      <InlinePageHeader title={withdrawPageTitle} onBack={handleBack} showBack={showInlineBack} />

      <div className="flex-1 overflow-y-auto pb-24">
        <div className="p-6 space-y-6">
          {activeTab === 'crypto' ? (
            <div className="space-y-6">
              {/* Selected Context Card */}
              {selectedCrypto && (
                <div className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-gray-800 rounded-xl">
                  <div className="relative">
                    <img
                      src={selectedCrypto.icon}
                      alt={selectedCrypto.currency}
                      className="w-10 h-10 rounded-full"
                      loading="lazy"
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = getCryptoIconFallback(selectedCrypto.currency);
                      }}
                    />
                    <img
                      src={selectedNetworkMeta?.icon}
                      alt="network"
                      className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full border-2 border-white dark:border-gray-800"
                      loading="lazy"
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        if (selectedNetworkMeta?.isInternal) {
                          e.currentTarget.src = INTERNAL_ICON;
                          return;
                        }
                        e.currentTarget.src = getCryptoIconFallback(selectedNetworkMeta?.sub);
                      }}
                    />
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold text-base flex items-center gap-2 text-gray-900 dark:text-gray-100">
                      {selectedCrypto.currency}
                      <span className="text-gray-400 dark:text-muted-foreground text-xs font-normal">in</span>
                      <span className="text-gray-900 dark:text-gray-100">{selectedNetworkMeta?.label}</span>
                    </div>
                    <div className="text-xs text-muted-foreground dark:text-gray-400">
                      {selectedNetworkMeta?.sub}
                    </div>
                  </div>
                </div>
              )}
              {/* Selected Context Card */}
              {/* Selected Context Card - Removed as requested, info moved to header */}

              {isInternalTransfer ? (
                <div className="space-y-4">
                  <div className="p-3 rounded-xl border border-sky-200 bg-sky-50 text-sky-700 text-sm">
                    Перевод внутри LightPay без комиссии
                  </div>
                  <div>
                    <label className="text-sm text-muted-foreground mb-2 block">Telegram username</label>
                    <Input
                      value={recipientUsername}
                      onChange={(e) => setRecipientUsername(e.target.value)}
                      placeholder="@username"
                      className="h-12 rounded-xl"
                    />
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Address Input */}
                  <div>
                    <label className="text-sm text-muted-foreground mb-2 block">{t('withdraw.wallet_address')}</label>
                    <Input
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                      placeholder=""
                      className="h-12 rounded-xl"
                    />
                    {shouldValidateAddressFormat && (
                      <div className={cn("text-xs mt-2 px-1 font-medium", isAddressFormatValid ? "text-green-600" : "text-red-500")}>
                        {isAddressFormatValid ? "Верный формат адреса" : "Неправильный формат адреса"}
                      </div>
                    )}
                  </div>

                  {/* Memo Input */}
                  {['XRP', 'XLM', 'TON', 'EOS', 'BTC'].includes(selectedCrypto?.currency) && (
                    <div>
                      <label className="text-sm text-muted-foreground mb-2 block">Memo / Tag (Optional)</label>
                      <Input
                        value={memo}
                        onChange={(e) => setMemo(e.target.value)}
                        placeholder=""
                        className="h-12 rounded-xl"
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            /* Fiat Form */
            <div className="space-y-4">
              <div className="p-4 bg-primary/5 border border-primary/10 rounded-xl">
                <p className="text-sm text-link">
                  {t('withdraw.fiat_notice', { user: supportHandle })}
                </p>
              </div>

              <div className="space-y-3">
                <Input
                  value={cardNumber}
                  onChange={(e) => setCardNumber(e.target.value)}
                  placeholder={t('withdraw.fiat_card_number')}
                  className="h-12 rounded-xl"
                />
                <Input
                  value={cardHolder}
                  onChange={(e) => setCardHolder(e.target.value)}
                  placeholder={t('withdraw.fiat_card_holder')}
                  className="h-12 rounded-xl"
                />
              </div>
            </div>
          )}

          {/* Amount Input */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="text-sm text-muted-foreground">
                {activeTab === 'fiat'
                  ? (fiatInputMode === 'USD' ? "Сумма (USD)" : "Сумма (RUB)")
                  : t('withdraw.amount_label', { currency: selectedCrypto?.currency || 'USDT' })}
              </label>
              {activeTab === 'fiat' ? (
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => { haptic('light'); setFiatInputMode('USD'); }}
                    className={cn(
                      "px-3 py-1 text-xs font-bold rounded-md transition-all",
                      fiatInputMode === 'USD' ? "text-link" : "text-muted-foreground/60"
                    )}
                  >
                    USD
                  </button>
                  <button
                    onClick={() => { haptic('light'); setFiatInputMode('RUB'); }}
                    className={cn(
                      "px-3 py-1 text-xs font-bold rounded-md transition-all",
                      fiatInputMode === 'RUB' ? "text-link" : "text-muted-foreground/60"
                    )}
                  >
                    RUB
                  </button>
                </div>
              ) : (
                <button
                  onClick={handleMax}
                  className="text-sm text-link font-medium"
                >
                  {t('common.max')}: {formatMinAmount(maxInputAmount)}
                </button>
              )}
            </div>

            <div className="relative">
              <Input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                onWheel={(e) => e.currentTarget.blur()}
                onBlur={() => {
                  if (activeTab !== 'crypto') return;
                  if (isInternalTransfer) return;
                  if (!(cryptoMin > 0)) return;
                  const n = parseFloat(amount);
                  if (!isFinite(n) || n <= 0) return;
                  if (n < cryptoMin) {
                    setAmount(String(formatMinAmount(cryptoMin)));
                  }
                }}
                placeholder="0.00"
                className={cn(
                  "h-14 text-xl font-mono pr-20 rounded-xl appearance-none",
                  (isOutOfRange || isBelowCryptoMin) && "border-red-500 ring-red-500 focus-visible:ring-red-500"
                )}
                inputMode="decimal"
                step="any"
                min={activeTab === 'crypto' && !isInternalTransfer && cryptoMin > 0 ? cryptoMin : undefined}
              />
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground font-medium">
                {activeTab === 'fiat' ? fiatInputMode : (selectedCrypto?.currency || 'USDT')}
              </span>
            </div>

            {activeTab === 'fiat' && amountNum > 0 && (
              <div className="space-y-2">
                <div className="p-4 bg-[var(--lp-tile)] rounded-xl border border-dashed border-[rgb(var(--lp-edge)/var(--lp-edge-a))] flex justify-between items-center animate-in fade-in slide-in-from-top-1">
                  <span className="text-sm text-muted-foreground font-medium">
                    {fiatInputMode === 'USD' ? t('withdraw.you_will_receive') : "Списание с баланса"}
                  </span>
                  <span className="text-xl font-bold text-foreground">
                    {fiatInputMode === 'USD'
                      ? formatRub(amountNum * withdrawalRate) + " RUB"
                      : (amountNum / withdrawalRate).toFixed(2) + " USD"
                    }
                  </span>
                </div>

                {isOutOfRange && (
                  <div className="flex items-center gap-2 rounded-xl border border-red-500/25 bg-red-500/10 px-3 py-2 text-xs font-medium text-red-500">
                    <AlertCircle className="h-4 w-4 shrink-0" />
                    <span>
                      {isBelowFiatMin
                        ? `Минимальная сумма вывода: ${formatRub(fiatMinRub)} ₽`
                        : `Сумма вывода ${formatRub(fiatMinRub)}-${formatRub(fiatMaxRub)} ₽`}
                    </span>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'crypto' && !isInternalTransfer && selectedCrypto && selectedNetwork && (
              <div className="flex items-start gap-2 rounded-xl border border-primary/15 bg-primary/5 px-3 py-2.5">
                <Info className="h-4 w-4 shrink-0 text-link mt-0.5" />
                <span className="text-xs text-muted-foreground leading-relaxed">
                  {feeData.loading || !feeKnown ? (
                    <>Считаем комиссию за вывод в сети {String(selectedNetwork || '').toUpperCase()}…</>
                  ) : (
                    <>
                      Комиссия за вывод — <span className="font-semibold text-foreground">{formatCryptoAmount(feeCoin)}&nbsp;{feeData.fee_currency || selectedCrypto?.currency || 'USDT'}</span>
                      {" "}(комиссия сети {String(selectedNetwork || '').toUpperCase()} и сервисный сбор). Спишется с баланса сверх суммы вывода.
                      {maxInputAmount > 0 && (
                        <>{" "}Доступно к выводу: <span className="font-semibold text-foreground">{formatCryptoAmount(maxInputAmount)}&nbsp;{selectedCrypto?.currency || 'USDT'}</span>.</>
                      )}
                    </>
                  )}
                </span>
              </div>
            )}

            {activeTab === 'crypto' && !isInternalTransfer && amountNum > 0 && address.length > 5 && (
              <div className="space-y-2 p-4 bg-[var(--lp-tile)] rounded-xl border border-dashed border-[rgb(var(--lp-edge)/var(--lp-edge-a))] animate-in fade-in slide-in-from-top-1">
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>Комиссия:</span>
                  <span>
                    {feeData.loading
                      ? "..."
                      : feeUnavailable
                        ? "н/д"
                        : `${formatCryptoAmount(feeData.fee || 0)} ${feeData.fee_currency || (selectedCrypto?.currency || '')}`}
                  </span>
                </div>
                <div className="flex justify-between items-center pt-2 border-t border-[var(--lp-hair)] mt-2">
                  <span className="text-sm font-semibold">Спишется с баланса:</span>
                  <span className="text-lg font-bold text-link">
                    {cryptoDebitAmount === null
                      ? "—"
                      : `${formatCryptoAmount(cryptoDebitAmount)} ${selectedCrypto?.currency || 'USDT'}`}
                  </span>
                </div>
              </div>
            )}

            {activeTab === 'crypto' && !isInternalTransfer && isBelowCryptoMin && (
              <div className="text-xs text-red-500 font-medium px-1">
                Минимальная сумма вывода: {formatMinAmount(cryptoMin)} {selectedCrypto?.currency || feeData.min_amount_currency || ''}
              </div>
            )}

            {activeTab === 'crypto' && shouldSuggestSwap && (
              <div className="p-3 rounded-xl border border-primary/20 bg-primary/5">
                <div className="text-sm text-link mb-2">
                  {hasNoCoinBalance
                    ? t('withdraw.swap_suggestion_no_balance', { coin: selectedCrypto?.currency || 'USDT' })
                    : t('withdraw.swap_suggestion_insufficient', { coin: selectedCrypto?.currency || 'USDT' })}
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleGoToSwap}
                  className="h-9 rounded-lg"
                >
                  {t('withdraw.swap_cta')}
                </Button>
              </div>
            )}

            {isCryptoInsufficient && activeTab === 'crypto' && (
              <div className="flex items-center justify-between gap-2 text-red-500 text-sm mt-2">
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  <span>
                    {t('withdraw.insufficient_balance')}: спишется {formatCryptoAmount(cryptoBalanceSpend)}{" "}
                    {selectedCrypto?.currency || 'USDT'} с комиссией {formatCryptoAmount(feeCoin)}, доступно{" "}
                    {formatCryptoAmount(cryptoSpendCeiling)}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={handleGoToSwap}
                  className="text-link font-medium"
                >
                  {t('withdraw.swap_cta')}
                </button>
              </div>
            )}

            {activeTab === 'fiat' && (
              <button
                onClick={handleMax}
                className="text-xs text-link font-medium w-full text-right mt-1"
              >
                {t('common.max')}: {fiatInputMode === 'USD' ? maxAmount.toFixed(2) : formatRub(maxAmount * withdrawalRate)}
              </button>
            )}
          </div>

          {/* Submit Button */}
          <Button
            onClick={handleSend}
            disabled={
              loading ||
              (
                activeTab === 'crypto'
                  ? (isInternalTransfer ? !recipientUsername.trim() : !normalizedAddress || isAddressFormatInvalid)
                  : !cardNumberValid
              ) ||
              !amount ||
              parseFloat(amount) <= 0 ||
              isOutOfRange ||
              isBelowCryptoMin ||
              isCryptoInsufficient
            }
            className={cn(
              "w-full h-14 rounded-full text-lg font-semibold shadow-lg",
              isOutOfRange ? "bg-red-500/50 hover:bg-red-500/50 grayscale" : "shadow-primary/20"
            )}
          >
            {loading ? (
              <div className="spinner mr-2" />
            ) : (
              activeTab === 'crypto' ? <Wallet className="w-5 h-5 mr-2" /> : <Building2 className="w-5 h-5 mr-2" />
            )}
            {isInternalTransfer ? "Перевести" : t('withdraw.button')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SendPage;
